import { useEffect, useState, useRef, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { LoadingButton } from '@mui/lab';
import { AgGridReact } from 'ag-grid-react';
import PropTypes from 'prop-types';
import { Stack, Box, Checkbox, CircularProgress, Dialog, DialogContent } from '@mui/material';
import axiosInstance from '../../utils/axios';
import { objectToFormData } from '../../utils/objectToFormData';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import AGGridHeaderCheckboxSimple from '../../reuseable-components/AGGrid/AGGridHeaderCheckboxSimple';

EditRole.propTypes = {
  userId: PropTypes.string,
  role: PropTypes.object,
  close: PropTypes.func
}

export default function EditRole({ role, close, userId }) {
  const gridRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [submitting, setSubmitting] = useState(false);
  const [loadingRoleList, setLoadingRoleList] = useState(false);
  const [possibleRolePermissions, setPossibleRolePermissions] = useState([]);

  useEffect(() => {
    if (role && userId) {
      setLoadingRoleList(true);
      axiosInstance.get(`/user/subRole?userId=${userId}&id=${role.id}`)
        .then(res => {
          if (res.data.modelValues?.length) {
            res.data.modelValues = res.data.modelValues.map(mValue => ({ modelId: mValue.value, modelLabel: mValue.label }));
            if (res.data.subModelValues?.length) res.data.modelValues = res.data.modelValues.flatMap(mValue => (res.data.subModelValues.map(sm => ({
              ...mValue,
              subModelId: sm.value,
              subModelLabel: sm.label,
            }))));
            if (res.data.subModelTwoValues?.length) res.data.modelValues = res.data.modelValues.flatMap(mValue => (res.data.subModelTwoValues.map(sm => ({
              ...mValue,
              subModelTwoId: sm.value,
              subModelTwoLabel: sm.label,
            }))));
            const activeVals = (res.data.activePermissionValues ?? [])
              .reduce((acc, permission) => {
                acc[`${permission.modelId}-${permission.subModelId}-${permission.subModelTwoId}`] = permission;
                return acc;
              }, {});
            res.data.modelValues = res.data.modelValues.map((permission, index) => {
              const activePermission = activeVals[`${permission.modelId}-${permission.subModelId ?? 0}-${permission.subModelTwoId ?? 0}`];        
              if (!activePermission) return { ...permission, active: false, allowAction: false, allowSubAction: false, allowSubActionTwo: false, id: index };
              return { ...permission, ...activePermission, active: true, id: index };
            })
          }
          setPossibleRolePermissions(res.data.modelValues ?? []);
        })
        .catch(() => enqueueSnackbar('Get role data failed', { variant: 'error' }))
        .finally(() => setLoadingRoleList(false));
    } else setPossibleRolePermissions([]);
  }, [role]);

  const onSubmitSubRole = () => {
    setSubmitting(true);
    const inputItems = [];
    gridRef?.current.api.forEachNode((node) => {
      if (node.data.active) inputItems.push({
        modelId: node.data.modelId,
        subModelId: node.data.subModelId,
        subModelTwoId: node.data.subModelTwoId,
        allowAction: node.data.allowAction,
        allowSubAction: node.data.allowSubAction,
        allowSubActionTwo: node.data.allowSubActionTwo
      });
    });
    axiosInstance.post("/user/updateSubRole", objectToFormData({ rolePermissions: inputItems, userId, rolePermissionId: role?.id }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar('Role updated', { variant: 'success' });
          close();
        }
        else enqueueSnackbar(`Update role failed`, { variant: 'error' })
      })
      .catch(() => enqueueSnackbar(`Update role failed`, { variant: 'error' }))
      .finally(() => setSubmitting(false));
  };

  const updateRowValue = (params, id, active) => {
    const newNodeData = { ...params.node.data };
    newNodeData[id] = active;
    params.node.setData(newNodeData);
    params.api.refreshHeader();
  }

  const updateAllColValues = (params, colId, active) => {
    params.api.forEachNodeAfterFilter((node) => {
      node.data[colId] = !active;
      return node.data.id;
    });
    params.api.refreshCells();
    params.api.refreshHeader();
  }

  const getCheckboxColBase = (id, header) => ({
    field: id,
    headerName: header,
    minWidth: 100,
    cellRenderer: (params) => <Checkbox checked={params.data[id] ?? false}
      onChange={(e) => updateRowValue(params, id, e.target.checked)} />,
    headerComponent: (params) => AGGridHeaderCheckboxSimple(params, (params, active) => updateAllColValues(params, id, active))
  });

  const columns = useMemo(() => [
    ...[
      role?.modelName ? { field: 'modelLabel', headerName: role.modelName, minWidth: 150, flex: 1 } : null,
      role?.subModelName ? { field: 'subModelLabel', headerName: role.subModelName, minWidth: 150 } : null,
      role?.subModelTwoName ? { field: 'subModelTwoLabel', headerName: role.subModelTwoName, minWidth: 150 } : null,
      role?.action ? getCheckboxColBase('allowAction', role.action) : null,
      role?.subAction ? getCheckboxColBase('allowSubAction', role.subAction) : null,
      role?.subActionTwo ? getCheckboxColBase('allowSubActionTwo', role.subActionTwo) : null,
      getCheckboxColBase('active', 'Active')
    ].filter(col => col)
  ], [role]);

  return <Dialog open={role != null} onClose={close} maxWidth="xl" >
    <DialogContent>
      <Stack spacing={1} minWidth="1100px">
        <AGGridQuickFilterWrapper gridRef={gridRef} />
        <Box>
          {!loadingRoleList ? <Box sx={{ height: "400px" }}>
            <AgGridReact
              ref={gridRef}
              {...AgGridDefaults}
              columnDefs={columns}
              rowData={possibleRolePermissions}
              onFilterChanged={(params) => params.api.refreshHeader()}
            />
          </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
        </Box>
        <Box>
          <LoadingButton loading={submitting} sx={{ display: "flex", justifySelf: "center", mb: 2, px: 7 }} color="purple" size="medium" variant="contained"
            onClick={onSubmitSubRole}>
            Save
          </LoadingButton>
        </Box>
      </Stack>
    </DialogContent>
  </Dialog>;
}

